import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

// ─── JSON-LD: FAQ + HowTo ──────────────────────────────────────────────────────
const SchemaMarkup = () => {
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "What is GraphQL and how is it different from a REST API?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "GraphQL is a query language for APIs. With a REST API, the server returns a fixed set of fields — often everything it knows about a contact. With GraphQL, you describe exactly which fields you need and only those are returned. For AI agents, this is critical: it reduces the token cost of parsing API responses by 80–95% compared to verbose REST payloads."
                }
            },
            {
                "@type": "Question",
                "name": "Does HubSpot have a GraphQL API?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. HubSpot has a CRM GraphQL API available at api.hubapi.com/collector/graphql. It is available on most HubSpot plans via a private app token with CRM read scopes. The rate limit is 500,000 complexity points per minute — generous enough to pull tens of thousands of contacts in a single session."
                }
            },
            {
                "@type": "Question",
                "name": "What is the JSON schema for a HubSpot GraphQL request?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Send a POST request to https://api.hubapi.com/collector/graphql with Content-Type: application/json and Authorization: Bearer YOUR_TOKEN. The body is: {\"query\": \"{ CRM { contact_collection(limit: 100, filter: { state__in: [\\\"IL\\\"] }) { total items { email firstname company } } } }\"}. You can filter by state, lifecycle stage, lead status, city, or any CRM property."
                }
            },
            {
                "@type": "Question",
                "name": "How much does it cost in AI tokens to pull a filtered HubSpot contact list with GraphQL?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "A GraphQL query pulling 100 contacts with 8 properties costs roughly 5,000–6,000 HubSpot complexity points and a fraction of a cent in AI tokens to process. Pulling and filtering 935 contacts across 10 paginated queries costs under $0.05 in token spend — compared to $100+ per hour using verbose REST responses parsed by an AI agent."
                }
            },
            {
                "@type": "Question",
                "name": "Can I use GraphQL to filter HubSpot contacts by location or lifecycle stage?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. HubSpot's CRM GraphQL supports filter operators including __in (match any of a list), __eq (exact match), __gt and __lt (comparisons). You can filter by state, city, lifecycle stage, lead status, industry, or any standard or custom property. Compound filters allow combining multiple conditions in a single query."
                }
            },
            {
                "@type": "Question",
                "name": "What is the RoofClaw Local Service Ad Agent?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "The RoofClaw LSA Agent is a free, MIT-licensed open source tool that polls Google Local Services Ads leads every 2 minutes, automatically responds to new inquiries, and pushes them into HubSpot or a compatible roofing CRM. It is available on GitHub at github.com/Roofing-Business-Partner/Roofclaw-LocalServiceAd-Agent. No subscription or setup fee required."
                }
            },
            {
                "@type": "Question",
                "name": "What is RoofClaw?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "RoofClaw is a physical AI agent — a dedicated Apple computer pre-configured with OpenClaw software — that ships to your roofing business office. It connects to your CRM, handles inbound leads, tracks insurance claims, manages pipeline follow-up, and runs 24/7. It ships in under two weeks at roofclaw.com."
                }
            }
        ]
    };

    const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Pull Targeted CRM Contacts With HubSpot GraphQL and Launch an Instantly Email Campaign",
        "description": "A step-by-step guide to using HubSpot's CRM GraphQL API to pull geographically targeted contacts, filter them by industry and lead status, load them into Instantly, and launch a personalized outbound email campaign — all via an AI agent.",
        "totalTime": "PT40M",
        "step": [
            {
                "@type": "HowToStep",
                "position": 1,
                "name": "Identify your target geography using storm or market data",
                "text": "Use web search or a weather/news source to identify regions with high activity relevant to your target audience. For roofing, this means recent hailstorm reports. Map these to US states for HubSpot filtering."
            },
            {
                "@type": "HowToStep",
                "position": 2,
                "name": "Write a HubSpot CRM GraphQL query filtered by state",
                "text": "POST to https://api.hubapi.com/collector/graphql with a query filtering contact_collection by state__in. Request only the fields you need: email, firstname, lastname, company, state, city, hs_lead_status, lifecyclestage. Use limit: 100 with offset-based pagination to retrieve all results."
            },
            {
                "@type": "HowToStep",
                "position": 3,
                "name": "Filter the results locally to your target audience",
                "text": "Parse the returned JSON. Exclude vendor companies, software tools, existing customers (Purchase Made), unqualified contacts, and contacts without a first name or valid email. Apply keyword matching on company name to confirm industry relevance."
            },
            {
                "@type": "HowToStep",
                "position": 4,
                "name": "Write a targeted email sequence with context-specific messaging",
                "text": "Craft 4–6 short emails (under 100 words each) around the specific pain point relevant to your target geography — in this case, storm season pipeline management. Lead with a high-value free offer in email 1 to maximize deliverability and open rates on subsequent emails."
            },
            {
                "@type": "HowToStep",
                "position": 5,
                "name": "Create a campaign in Instantly via API and load leads",
                "text": "POST to https://api.instantly.ai/api/v2/campaigns to create the campaign with your sequence. Then POST individual leads to https://api.instantly.ai/api/v2/leads with first_name, last_name, email, company_name, and any custom variables used in your templates (e.g. city)."
            },
            {
                "@type": "HowToStep",
                "position": 6,
                "name": "Activate the campaign",
                "text": "POST to https://api.instantly.ai/api/v2/campaigns/{campaign_id}/activate. The campaign will begin sending on the configured schedule, stop on reply, and BCC your CRM on every send."
            }
        ]
    };

    return (
        <>
            <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        </>
    );
};

// ─── Infographic: The GraphQL Efficiency Numbers ────────────────────────────────
const GraphQLVsRest = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            GraphQL vs REST: Token Cost Comparison
        </p>
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-red-500/5 border border-red-500/20 rounded-xl">
                    <div className="text-xs font-data uppercase tracking-wider text-red-400 mb-3">REST API (old approach)</div>
                    <div className="space-y-2 text-sm text-[#F0EFF4]/60">
                        <div>• Returns 40–60 fields per contact</div>
                        <div>• ~95% of data you never use</div>
                        <div>• AI agent parses full JSON blob</div>
                        <div>• Token burn: <span className="text-red-400 font-bold">~$100/hr at scale</span></div>
                        <div>• Slow. Expensive. Noisy.</div>
                    </div>
                </div>
                <div className="p-6 bg-green-500/5 border border-green-500/20 rounded-xl">
                    <div className="text-xs font-data uppercase tracking-wider text-green-400 mb-3">GraphQL (what we used)</div>
                    <div className="space-y-2 text-sm text-[#F0EFF4]/60">
                        <div>• Returns exactly 8 fields per contact</div>
                        <div>• 0% waste — you asked, you got</div>
                        <div>• 5,428 complexity pts per 100 contacts</div>
                        <div>• Token cost: <span className="text-green-400 font-bold">under $0.05 total</span></div>
                        <div>• Fast. Lean. Precise.</div>
                    </div>
                </div>
            </div>
            <div className="mt-6 p-4 bg-[#0A0A14] rounded-xl border border-[#F0EFF4]/5">
                <p className="text-xs text-[#F0EFF4]/40 text-center font-data">
                    Rate limit: 500,000 pts/min · Used: ~54,280 pts for all 935 contacts · Remaining: 445,720 pts
                </p>
            </div>
        </div>
    </div>
);

// ─── Infographic: Campaign Stats ────────────────────────────────────────────────
const CampaignStats = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            Campaign: RoofClaw — Storm Season Pipeline (IL/MO/WI)
        </p>
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                    { label: 'Leads Loaded', value: '560', sub: '0 failures' },
                    { label: 'Daily Send Cap', value: '150', sub: 'across 9 addresses' },
                    { label: 'Email Sequence', value: '5', sub: 'over 17 days' },
                    { label: 'Time to Launch', value: '~40m', sub: 'from brief to live' },
                ].map((s, i) => (
                    <div key={i} className="p-4 bg-[#0A0A14] rounded-xl border border-[#F0EFF4]/5 text-center">
                        <div className="text-2xl font-bold font-heading text-[#7B61FF]">{s.value}</div>
                        <div className="text-xs font-data uppercase tracking-wider text-[#F0EFF4]/60 mt-1">{s.label}</div>
                        <div className="text-xs text-[#F0EFF4]/30 mt-1">{s.sub}</div>
                    </div>
                ))}
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                {[
                    { state: 'Illinois', count: 275, cities: 'Chicago NW suburbs, Schaumburg, Arlington Heights', storm: 'Apr 16–17', color: 'blue' },
                    { state: 'Missouri', count: 153, cities: 'Springfield, St. Louis, Kansas City', storm: 'Apr 27–28', color: 'purple' },
                    { state: 'Wisconsin', count: 132, cities: 'Madison, Milwaukee, Waukesha', storm: 'Apr 14–15', color: 'green' },
                ].map((s, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${
                        s.color === 'blue' ? 'bg-blue-500/5 border-blue-500/20' :
                        s.color === 'purple' ? 'bg-purple-500/5 border-purple-500/20' :
                        'bg-green-500/5 border-green-500/20'
                    }`}>
                        <div className={`text-xs font-data uppercase tracking-wider mb-2 ${
                            s.color === 'blue' ? 'text-blue-400' :
                            s.color === 'purple' ? 'text-purple-400' : 'text-green-400'
                        }`}>{s.state} · {s.count} roofers</div>
                        <div className="text-xs text-[#F0EFF4]/50">{s.cities}</div>
                        <div className="text-xs text-[#F0EFF4]/30 mt-1">Storm: {s.storm}</div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

// ─── Code Block ────────────────────────────────────────────────────────────────
const CodeBlock = ({ children, label }) => (
    <div className="my-8 not-prose">
        {label && <p className="text-xs font-data uppercase tracking-widest text-[#7B61FF]/70 mb-2">{label}</p>}
        <pre className="bg-[#0A0A14] border border-[#F0EFF4]/10 rounded-xl p-6 overflow-x-auto text-sm text-green-400 font-data leading-relaxed">
            <code>{children}</code>
        </pre>
    </div>
);

// ─── Email Sequence Preview ─────────────────────────────────────────────────────
const SequencePreview = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            The 5-Email Storm Sequence
        </p>
        <div className="space-y-3">
            {[
                { day: 'Day 0', subject: 'free tool for roofers in {{city}} — no catch', angle: 'Free open-source LSA agent. GitHub repo. No pitch.', tag: 'High Value Give' },
                { day: 'Day 3', subject: 'the homeowner called 3 roofers. you were #2.', angle: 'Speed to lead. First responder wins the storm job.', tag: 'Speed to Lead' },
                { day: 'Day 7', subject: 'most storm jobs die waiting on the adjuster', angle: 'Claim tracking & follow-up automation.', tag: 'Claim Follow-Up' },
                { day: 'Day 12', subject: 'your Google LSA leads are going to someone else', angle: '2-minute LSA polling. Inspection booked before competitor answers.', tag: 'LSA + Schedule' },
                { day: 'Day 17', subject: 'storm season is 60 days. your pipeline is permanent.', angle: 'Ships in 2 weeks. See it, touch it, put it on your desk.', tag: 'Close' },
            ].map((e, i) => (
                <div key={i} className="flex gap-4 items-start p-4 bg-[#0A0A14] rounded-xl border border-[#F0EFF4]/5">
                    <div className="flex-shrink-0 w-14 text-center">
                        <span className="text-xs font-data text-[#7B61FF] uppercase tracking-wider">{e.day}</span>
                    </div>
                    <div className="w-px bg-[#7B61FF]/20 self-stretch mx-2" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-[#F0EFF4] mb-1 font-data">"{e.subject}"</div>
                        <div className="text-xs text-[#F0EFF4]/40">{e.angle}</div>
                    </div>
                    <div className="flex-shrink-0 text-xs font-data px-2 py-1 rounded-full bg-[#7B61FF]/10 text-[#7B61FF] border border-[#7B61FF]/20 whitespace-nowrap">
                        {e.tag}
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// ─── FAQ Section ───────────────────────────────────────────────────────────────
const FAQSection = () => (
    <div className="my-16 not-prose">
        <h2 className="text-3xl font-heading font-bold mb-8 text-[#F0EFF4]">FAQ: The Technical Stuff</h2>
        <div className="space-y-4">
            {[
                {
                    q: "Do I need a special HubSpot plan for GraphQL?",
                    a: "The CRM GraphQL API is available on most HubSpot plans. You access it with a private app token that has CRM read scopes. The endpoint is https://api.hubapi.com/collector/graphql."
                },
                {
                    q: "What's the JSON schema for a GraphQL request to HubSpot?",
                    a: `Simple POST with a JSON body:\n\n{ "query": "{ CRM { contact_collection(limit: 100, filter: { state__in: [\\"IL\\"] }) { total items { email firstname company } } } }" }\n\nHeader: Authorization: Bearer YOUR_PRIVATE_APP_TOKEN\nContent-Type: application/json`
                },
                {
                    q: "What's the rate limit?",
                    a: "500,000 complexity points per minute, 300 million per day. A typical filtered contact pull with 8 properties costs about 5,000–6,000 points. You'd have to work hard to hit the limit."
                },
                {
                    q: "Can I filter by multiple fields?",
                    a: "Yes. HubSpot's CRM GraphQL supports __in, __eq, __gt, __lt, and compound filters. You can filter by lifecycle stage, lead status, state, city, industry — whatever properties exist on your contacts."
                },
                {
                    q: "How is this different from HubSpot's List tool?",
                    a: "Lists are great for marketing workflows inside HubSpot. GraphQL is for when you want the raw data to do something outside HubSpot — like loading into Instantly, building a CSV, or feeding an AI agent."
                },
                {
                    q: "Does this work with other CRMs?",
                    a: "Many modern CRMs support GraphQL. Salesforce has had it for years. Even if your CRM only has REST, you can use aggressive field filtering (?properties=email,firstname,company) to reduce token cost significantly."
                },
            ].map((item, i) => (
                <div key={i} className="p-6 bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl">
                    <h3 className="text-base font-bold text-[#F0EFF4] mb-3">{item.q}</h3>
                    <p className="text-sm text-[#F0EFF4]/60 leading-relaxed whitespace-pre-line">{item.a}</p>
                </div>
            ))}
        </div>
    </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────────
const HubspotGraphqlStormCampaign = () => (
    <>
        <SchemaMarkup />
        <BlogLayout
            title="I Launched a 560-Lead Storm Campaign From My Hummer. Here's the GraphQL Trick That Made It Free."
            date="2026-04-30"
            readTime="7"
            heroImage="/images/blog/hubspot-graphql-storm-campaign-hero.jpg"
            description="How we used HubSpot's CRM GraphQL API to pull geographically targeted roofers, write a storm-season email sequence, and launch a live Instantly campaign — all from Telegram, in under 40 minutes, for almost nothing in token cost."
        >
            <p>
                I was driving to grab nicotine pouches. My AI agent — Vito, who runs RoofClaw sales — messaged me on Telegram asking how I wanted to target our Instantly campaign.
            </p>
            <p>
                I typed back from a parking lot: <em>"Big hailstorms just hit Illinois, Missouri, and Wisconsin. Pull roofers from HubSpot in those states and build a storm sequence."</em>
            </p>
            <p>
                Forty minutes later, 560 targeted roofers were loaded into a live campaign. Five-email sequence. A/B subject lines. 150 sends a day. BCC'd to HubSpot. Firing.
            </p>
            <p>
                I was back home before it finished.
            </p>
            <p>
                Here's exactly how it worked — and the part that made the whole thing nearly free.
            </p>

            <h2>The $100/Hour Problem</h2>
            <p>
                The day before, Vito had burned about $100 in AI tokens in an hour just setting up Instantly infrastructure. The culprit: pulling HubSpot contacts via the standard REST API.
            </p>
            <p>
                The REST contacts endpoint returns a massive blob — every property on every contact, most of which you never asked for. An AI agent parsing that data burns tokens on fields like <code>hs_analytics_last_referrer</code> and <code>recent_conversion_event_name</code> that have nothing to do with the task. At scale, it adds up fast.
            </p>
            <p>
                We needed a smarter approach. That approach is GraphQL.
            </p>

            <h2>What Is GraphQL?</h2>
            <p>
                GraphQL is a query language for APIs. Instead of calling a fixed endpoint that returns whatever the server decides to give you, you write a query that describes <strong>exactly</strong> what you want — and you get back exactly that. Nothing more.
            </p>
            <p>
                The mental model:
            </p>
            <blockquote>
                <strong>REST:</strong> "Give me everything you know about all my contacts."<br />
                <em>Server returns 47 fields per contact. Most of it noise.</em>
                <br /><br />
                <strong>GraphQL:</strong> "Give me email, first name, company, city, and state — only contacts in IL, MO, or WI."<br />
                <em>Server returns those 5 fields for those contacts. That's it.</em>
            </blockquote>
            <p>
                HubSpot has a CRM GraphQL API at <code>api.hubapi.com/collector/graphql</code>. Most people don't know it exists. Here's the query we actually ran:
            </p>

            <CodeBlock label="The GraphQL Query — Exact Copy">
{`{
  CRM {
    contact_collection(
      limit: 100,
      offset: 0,
      filter: {
        state__in: ["WI", "Wisconsin", "MO", "Missouri", "IL", "Illinois"]
      }
    ) {
      total
      offset
      items {
        hs_object_id
        email
        firstname
        lastname
        company
        state
        city
        hs_lead_status
        lifecyclestage
      }
    }
  }
}`}
            </CodeBlock>

            <p>
                That single query pulled 100 contacts. We paginated through all 935 contacts in the three states with 10 calls. Total complexity cost: <strong>54,280 points</strong> out of a 500,000/minute limit. We used about 11% of our per-minute budget to pull the entire regional dataset.
            </p>

            <GraphQLVsRest />

            <h2>The Filtering Logic</h2>
            <p>
                935 contacts across three states doesn't mean 935 roofers. HubSpot accumulates everyone — software vendors, ex-customers, HubSpot employees (yes, those end up in there), people with no first name. We filtered locally:
            </p>
            <ul>
                <li><strong>Out:</strong> software vendors and non-roofing companies (HubSpot, SumoQuote, Jobba, PartnerStack)</li>
                <li><strong>Out:</strong> existing RoofClaw customers (lead status: Purchase Made)</li>
                <li><strong>Out:</strong> unqualified / Do Not Contact contacts</li>
                <li><strong>Out:</strong> anyone without a valid email or first name</li>
                <li><strong>In:</strong> company names matching roofing keywords — roof, exterior, restoration, contracting, siding, gutter, construction, and 20 more</li>
            </ul>
            <p>
                935 contacts → <strong>560 clean roofers</strong>.
            </p>

            <h2>Why Storm Targeting?</h2>
            <p>
                We pulled recent hailstorm data and found three active zones:
            </p>
            <ul>
                <li><strong>Wisconsin</strong> — Baseball-sized hail hit Madison and southern WI on April 14–15. Some stones reached 4 inches near Madison's East Side.</li>
                <li><strong>Missouri</strong> — Softball and grapefruit-sized hail struck Springfield, St. Louis, and Kansas City on April 27–28. Still fresh.</li>
                <li><strong>Illinois</strong> — Chicago's northwest suburbs (Des Plaines, Schaumburg, Arlington Heights) hit April 16–17.</li>
            </ul>
            <p>
                In storm season, the problem isn't leads — roofers are coming back to the office saying "we've got 400 leads this week." The problem is <strong>pipeline management</strong>. Capturing every one of them, staying intentional on follow-up, and using AI to keep the momentum going so the storm converts to revenue.
            </p>
            <p>
                That's the campaign angle.
            </p>

            <SequencePreview />

            <h2>Email 1: The Move</h2>
            <p>
                Email 1 is the one I'm most proud of. Instead of a pitch, we led with a completely free open-source tool we built — the RoofClaw LSA Agent.
            </p>
            <blockquote>
                "It's free. MIT license. No subscription, no setup fee, nothing. Point your AI agent at this repo and it'll walk you through the whole setup. You don't need us."
            </blockquote>
            <p>
                Zero friction. Zero reason to hit unsubscribe. If email 1 gives away a working tool with a GitHub link, the roofer who actually uses it is already warm when email 2 lands three days later.
            </p>
            <p>
                The repo: <a href="https://github.com/Roofing-Business-Partner/Roofclaw-LocalServiceAd-Agent" target="_blank" rel="noopener noreferrer">github.com/Roofing-Business-Partner/Roofclaw-LocalServiceAd-Agent</a>
            </p>

            <h2>Loading Into Instantly</h2>
            <p>
                Instantly's v2 API takes one lead object per POST. We ran parallel batches of 10 with 200ms spacing between batches — clean, no rate limit issues.
            </p>

            <CodeBlock label="Lead Upload — Single Object Format">
{`POST https://api.instantly.ai/api/v2/leads
Authorization: Bearer YOUR_KEY
Content-Type: application/json

{
  "campaign_id": "YOUR_CAMPAIGN_ID",
  "email": "jason@cornerstone-restoration.com",
  "first_name": "Jason",
  "last_name": "Chi",
  "company_name": "Cornerstone Restoration Inc",
  "city": "Schaumburg",
  "state": "IL"
}`}
            </CodeBlock>

            <p>
                560 leads. 0 failures. Campaign activated via <code>POST /api/v2/campaigns/{'{id}'}/activate</code>.
            </p>

            <CampaignStats />

            <FAQSection />

            <h2>The Actual Takeaway</h2>
            <p>
                The whole workflow — storm research, HubSpot pull, list cleaning, sequence writing, Instantly campaign creation, lead loading, activation — was done in one Telegram conversation from a parking lot in a Hummer.
            </p>
            <p>
                The AI didn't just execute instructions. It figured out the GraphQL schema by testing it live, identified the storm zones from a web search, wrote the filtering logic, and optimized the API calls after we flagged the token burn problem from the day before. It self-corrected and shipped.
            </p>
            <p>
                That's what a properly configured AI agent looks like in practice. Not a chatbot you ask questions to. A system that does the work.
            </p>
            <p>
                If you want to see how RoofClaw does this for roofing businesses — handling pipeline, claim follow-up, speed to lead, and schedule optimization — it ships in under two weeks, pre-configured for your CRM. You can see it, touch it, and put it on your desk.
            </p>
            <p>
                Or just use the free tool. Either way, you win.
            </p>
            <p>
                <a href="https://github.com/Roofing-Business-Partner/Roofclaw-LocalServiceAd-Agent" target="_blank" rel="noopener noreferrer">
                    github.com/Roofing-Business-Partner/Roofclaw-LocalServiceAd-Agent
                </a>
            </p>
        </BlogLayout>
    </>
);

export default HubspotGraphqlStormCampaign;
